import { GAME_FEATURES, SECTION_NAMES, SPJ_JACKPOT_GAMES } from '@repo/common';
import type IGameSettings from 'types/models/IGameSettings';
import { getSectionGameKeys } from 'store/app/appStore';
import {
  SEARCH_FILTERS,
  SEARCH_JACKPOT_OPTIONS,
  SEARCH_MAX_WIN_BOUNDS,
  SEARCH_MAX_WIN_OPTIONS,
} from '../constants/searchConstants';
import { getPremiumLinkGameKeys } from 'features/jackpots/components/variants/PremiumLink/hooks/usePremiumLinkGameKeys';

/**
 * Helper functions for normalizing strings for case-insensitive comparison
 */
export const normalizeString = (str: string) => str.trim().toLowerCase();

export const getNormalizedGameFeatures = (gameFeatures: string[] | undefined): Set<string> => {
  return new Set((gameFeatures ?? []).filter((feature) => feature !== GAME_FEATURES.HIGH_RTP).map(normalizeString));
};

/**
 * Functions to categorize games into filter buckets based on their properties
 */
type GameCategorizationCategory = typeof SEARCH_FILTERS.MAX_WIN | typeof SEARCH_FILTERS.JACKPOT;

const categorizeMaxWin = (maxAwardingCoef: number | null | undefined): string | null => {
  if (typeof maxAwardingCoef !== 'number') return null;

  if (maxAwardingCoef <= SEARCH_MAX_WIN_BOUNDS.UP_TO_1200) {
    return SEARCH_MAX_WIN_OPTIONS.UP_TO_1200;
  }

  if (maxAwardingCoef <= SEARCH_MAX_WIN_BOUNDS.UP_TO_3000) {
    return SEARCH_MAX_WIN_OPTIONS.FROM_1200_TO_3000;
  }

  if (maxAwardingCoef <= SEARCH_MAX_WIN_BOUNDS.UP_TO_8000) {
    return SEARCH_MAX_WIN_OPTIONS.FROM_3000_TO_8000;
  }

  return SEARCH_MAX_WIN_OPTIONS.OVER_8000;
};

const categorizeJackpot = (game: IGameSettings): string | null => {
  if (game.gameFeatures?.includes(GAME_FEATURES.HIGH_RTP)) {
    return GAME_FEATURES.HIGH_RTP;
  }

  const premiumLinkGameKeys = getPremiumLinkGameKeys();

  if (game.jackpotName && premiumLinkGameKeys.includes(game.gameKey) && SPJ_JACKPOT_GAMES.includes(game.jackpotName)) {
    return SEARCH_JACKPOT_OPTIONS.PREMIUM_LINK;
  }

  if (game.displayJackpotName) {
    return game.displayJackpotName;
  }

  const progressiveGameKeys = getSectionGameKeys(SECTION_NAMES.PROGRESSIVE);

  if (game.jackpotName && progressiveGameKeys.includes(game.gameKey) && SPJ_JACKPOT_GAMES.includes(game.jackpotName)) {
    return SEARCH_JACKPOT_OPTIONS.PROGRESSIVE;
  }

  return game.jackpotName ?? null;
};

export const categorizeGame = (game: IGameSettings, category: GameCategorizationCategory): string | null => {
  switch (category) {
    case SEARCH_FILTERS.MAX_WIN:
      return categorizeMaxWin(game.maxAwardingCoef);
    case SEARCH_FILTERS.JACKPOT:
      return categorizeJackpot(game);
    default:
      return null;
  }
};
